import React, {Component} from 'react';

import {hideErrorMessage, loadUserData} from '../usercontext/user.action';
import {connect, useDispatch} from 'react-redux';
import UserContext from '../usercontext/UserContext.component';
import * as logger from '../../utils/logger.function';
import ErrorMessagePopup from '../popup/ErrorMessagePopup.component';
import ModalRouter from 'components/modals/ModalRouter';
import {useTranslation} from 'components/translations/useTranslation';
import {MODAL_ACTIONS} from 'utils/constants';
import {launchDarkly} from 'utils/launchDarkly';
import {AccessDenied, WithoutStartup} from '../errorpage/ErrorPage.component.jsx';
import {
    getInitialized,
    getHydrated,
    getErrorMessageVisibility,
    getAccessDenied,
    getStartupFailed,
} from "../store/application.reducers";
import * as reduxSelectors from "../store/application.reducers";
import {snowplowInit} from "utils/snowplow.js";

/**
 * renders the global error message - either as the legacy popup or, behind the use-new-modals flag,
 * as the gaia modal
 */
export const ErrorMessageDialog = ({error}) => {
    const useNewModals = launchDarkly.companyClient?.variation("use-new-modals", false);
    const {translate} = useTranslation();
    const dispatch = useDispatch();

    /**
     * this router hosts a single modal, whose visibility is owned by the showErrorMessage state and not by
     * a modal type kept in the local state - so the only transition it can be asked for is the close
     */
    const closeDialog = React.useCallback(() => dispatch(hideErrorMessage()), [dispatch]);

    if (!useNewModals) {
        return <ErrorMessagePopup error={error}/>;
    }

    const title = error?.title
        ? (error.applyTranslations ? translate(error.title) : error.title)
        : translate("popUp.error.title");
    const label = translate("popUp.error.label")

    return (
        <ModalRouter type={MODAL_ACTIONS.ERROR_MESSAGE}
                     more={{...error, title, label}}
                     toggleModal={closeDialog}/>
    );
};

/**
 * container/wrapper component, responsible for proper user context handling
 * ~ check user context on each page refresh, after the store state is hydrated
 * ~ display application after the previous finished
 */

export class AppInitialization extends Component {

    componentDidMount() {
        if (!this.props.showErrorMessage && !this.props.accessDenied && !this.props.startupFailed) {
            if (this.props.hydrated && !this.props.initialized) {
                // we need the store in sync with actual session from backend, so emit action after store hydration
                logger.debug("store.state hydrated, dispatching action to load user context");
                this.loadUserData();
            }
        }
    }
    componentDidUpdate(prevProps) {
        if (prevProps.hydrated !== this.props.hydrated || prevProps.initialized !== this.props.initialized || this.props.showErrorMessage !== prevProps.showErrorMessage) {
            if (!this.props.showErrorMessage && !this.props.accessDenied && !this.props.startupFailed) {
                if (this.props.hydrated && !this.props.initialized) {
                    // we need the store in sync with actual session from backend, so emit action after store hydration
                    logger.debug("store.state hydrated, dispatching action to load user context");
                    this.loadUserData();
                }
            }
        }
    }

    loadUserData() {
        this.props.loadUserData().then(() => {
            snowplowInit(this.props.userConfiguration);
        });
    }

    render() {
        /**
         * not sure why, but this has to be here :)
         * in case I've moved that into componentDidMount, then it won't get triggered for refreshing in myTasks
         * if I move it to componentWillReceiveProps, then it won't get triggered for refreshing for example taskDetail
         */
        /*if(!this.props.showErrorMessage) {
            if (this.props.hydrated && !this.props.initialized) {
                // we need the store in sync with actual session from backend, so emit action after store hydration
                logger.debug("store.state hydrated, dispatching action to load user context");
                this.props.loadUserData();
            }
        }*/

        if (this.props.accessDenied) {
            // user has no access to Approval (/startup returned 403) - render the access denied page without the
            // nav shell, so no menu is shown when the user has no access rights
            return (
                <div className="app-width full-height-container container-fluid">
                    <AccessDenied/>
                </div>
            );
        }

        if (this.props.startupFailed) {
            // /startup couldn't be loaded after exhausting retries - render the server down page in place.
            // We can't route to /error here: the route only renders once the app is initialized.
            return (
                <div className="app-width full-height-container container-fluid">
                    <WithoutStartup/>
                </div>
            );
        }

        if (!this.props.initialized) {
            return (
                <div className="col-md-12">
                    {this.props.showErrorMessage &&
                        <ErrorMessageDialog error={this.props.showErrorMessage}/>
                    }
                    <span className="appicon icon loading"/>
                </div>
            );
        } else {
            const propsForChildren = Object.assign({}, this.props);
            delete propsForChildren.initialized;
            delete propsForChildren.hydrated;

            return (
                <div>
                    <UserContext {...propsForChildren} />
                    {/* rendered after the page content, so the error dialog stacks on top of any modal already open */}
                    {this.props.showErrorMessage &&
                        <ErrorMessageDialog error={this.props.showErrorMessage}/>
                    }
                </div>
            );
        }
    }
}


const mapStateToProps = function (store) {
    return {
        // local : store
        initialized: getInitialized(store),
        hydrated: getHydrated(store),
        accessDenied: getAccessDenied(store),
        startupFailed: getStartupFailed(store),
        showErrorMessage: getErrorMessageVisibility(store),
        userConfiguration: reduxSelectors.getUsersData(store)
    };
};


export default connect(mapStateToProps, {
    loadUserData
})(AppInitialization)